<template>
  <v-layout align-start>
    <v-flex>

      <!-- Header -->
      <v-toolbar flat color="transparent" class="mb-2">
        <v-toolbar-title class="font-weight-bold">
          Solicitudes de Afiliación
        </v-toolbar-title>
        <v-chip v-if="tab === 0 && filteredPending.length" small color="orange" dark class="ml-2">
          {{ filteredPending.length }} pendiente{{ filteredPending.length !== 1 ? 's' : '' }}
        </v-chip>
        <v-divider class="mx-3" inset vertical></v-divider>
        <v-btn-toggle v-model="tab" mandatory dense class="mr-3">
          <v-btn small :value="0">
            <v-icon small left>hourglass_empty</v-icon>Pendientes
          </v-btn>
          <v-btn small :value="1">
            <v-icon small left>archive</v-icon>Archivados
          </v-btn>
          <v-btn small :value="2">
            <v-icon small left>check_circle</v-icon>Aprobados
          </v-btn>
        </v-btn-toggle>
        <v-text-field
          v-model="search"
          append-icon="search"
          label="Buscar..."
          single-line hide-details dense outlined
          style="max-width: 220px"
        ></v-text-field>
      </v-toolbar>

      <!-- Pending table -->
      <v-data-table
        v-if="tab === 0"
        :headers="pendingHeaders"
        :items="filteredPending"
        :search="search"
        class="elevation-1"
        sort-by="createdAt"
        sort-desc
      >
        <!-- Avatar + nombre -->
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-1">
            <v-avatar size="34" color="rgba(139,92,246,0.22)" class="mr-2">
              <span class="font-weight-bold" style="color:#c4b5fd; font-size:13px">
                {{ initials(item.name, item.surName) }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-medium" style="font-size:13px">{{ item.name }} {{ item.surName }}</div>
              <div class="grey--text text--darken-1" style="font-size:11px">{{ item.email }}</div>
            </div>
          </div>
        </template>

        <!-- Teléfono + WhatsApp -->
        <template v-slot:item.phone="{ item }">
          <div class="d-flex align-center">
            <span style="font-size:13px">{{ item.phone || '—' }}</span>
            <v-btn
              v-if="item.phone"
              icon x-small class="ml-1"
              :href="whatsappLink(item.phone)"
              target="_blank"
              title="Abrir WhatsApp"
            >
              <v-icon small color="green darken-1">chat</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- Departamento / Localidad -->
        <template v-slot:item.department="{ item }">
          <span v-if="item.department" style="font-size:12px">{{ item.department }}<span v-if="item.locality">, {{ item.locality }}</span></span>
          <span v-else class="grey--text">—</span>
        </template>

        <!-- Comentario -->
        <template v-slot:item.registrationComment="{ item }">
          <v-tooltip bottom v-if="item.registrationComment" max-width="400">
            <template v-slot:activator="{ on }">
              <v-chip v-on="on" x-small outlined color="blue-grey" style="max-width:160px; cursor:pointer">
                <span class="text-truncate" style="max-width:140px">{{ item.registrationComment }}</span>
              </v-chip>
            </template>
            <span>{{ item.registrationComment }}</span>
          </v-tooltip>
          <span v-else class="grey--text">—</span>
        </template>

        <!-- Fecha -->
        <template v-slot:item.createdAt="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span v-on="on" class="text--secondary" style="font-size:12px">{{ timeAgo(item.createdAt) }}</span>
            </template>
            <span>{{ formatDate(item.createdAt) }}</span>
          </v-tooltip>
        </template>

        <!-- Acciones -->
        <template v-slot:item.opciones="{ item }">
          <v-btn x-small color="success" dark class="mr-1" @click="confirmApprove(item)">
            <v-icon x-small left>check</v-icon>Aprobar
          </v-btn>
          <v-btn x-small color="error" outlined @click="openRejectModal(item)">
            <v-icon x-small left>close</v-icon>Rechazar
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="text-center pa-8 grey--text">
            <v-icon large color="grey darken-1">inbox</v-icon>
            <div class="mt-2">No hay solicitudes pendientes</div>
          </div>
        </template>
      </v-data-table>

      <!-- Archived table -->
      <v-data-table
        v-if="tab === 1"
        :headers="archivedHeaders"
        :items="filteredArchived"
        :search="search"
        class="elevation-1"
        sort-by="rejectedAt"
        sort-desc
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-1">
            <v-avatar size="30" color="grey lighten-3" class="mr-2">
              <span class="grey--text text--darken-1 font-weight-bold" style="font-size:11px">
                {{ initials(item.name, item.surName) }}
              </span>
            </v-avatar>
            <div>
              <div style="font-size:13px">{{ item.name }} {{ item.surName }}</div>
              <div class="grey--text" style="font-size:11px">{{ item.email }}</div>
            </div>
          </div>
        </template>
        <template v-slot:item.createdAt="{ item }">
          <span style="font-size:12px">{{ formatDate(item.createdAt) }}</span>
        </template>
        <template v-slot:item.rejectedAt="{ item }">
          <span style="font-size:12px">{{ formatDate(item.rejectedAt) }}</span>
        </template>
        <template v-slot:item.rejectedByName="{ item }">
          <span style="font-size:12px">{{ item.rejectedByName || '—' }}</span>
        </template>
        <template v-slot:item.rejectionReason="{ item }">
          <v-tooltip bottom v-if="item.rejectionReason" max-width="400">
            <template v-slot:activator="{ on }">
              <span v-on="on" class="text-truncate d-inline-block" style="max-width:200px; font-size:12px; cursor:pointer; color:#e53935">
                {{ item.rejectionReason }}
              </span>
            </template>
            <span>{{ item.rejectionReason }}</span>
          </v-tooltip>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:no-data>
          <div class="text-center pa-8 grey--text">
            <v-icon large color="grey darken-1">archive</v-icon>
            <div class="mt-2">No hay solicitudes archivadas</div>
          </div>
        </template>
      </v-data-table>

      <!-- Approved table -->
      <v-data-table
        v-if="tab === 2"
        :headers="approvedHeaders"
        :items="filteredApproved"
        :search="search"
        class="elevation-1"
        sort-by="approvedAt"
        sort-desc
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-1">
            <v-avatar size="30" color="rgba(129,199,132,0.2)" class="mr-2">
              <span class="green--text text--darken-2 font-weight-bold" style="font-size:11px">
                {{ initials(item.name, item.surName) }}
              </span>
            </v-avatar>
            <div>
              <div style="font-size:13px">{{ item.name }} {{ item.surName }}</div>
              <div class="grey--text" style="font-size:11px">{{ item.email }}</div>
            </div>
          </div>
        </template>
        <template v-slot:item.createdAt="{ item }">
          <span style="font-size:12px">{{ formatDate(item.createdAt) }}</span>
        </template>
        <template v-slot:item.approvedAt="{ item }">
          <span style="font-size:12px">{{ formatDate(item.approvedAt) }}</span>
        </template>
        <template v-slot:item.approvedByName="{ item }">
          <span style="font-size:12px">{{ item.approvedByName || '—' }}</span>
        </template>
        <template v-slot:item.type_affiliate="{ item }">
          <v-chip x-small :color="categoryColor(item.type_affiliate)" dark>{{ item.type_affiliate || '—' }}</v-chip>
        </template>
        <template v-slot:no-data>
          <div class="text-center pa-8 grey--text">
            <v-icon large color="grey darken-1">check_circle</v-icon>
            <div class="mt-2">No hay afiliados aprobados</div>
          </div>
        </template>
      </v-data-table>

      <!-- Modal Aprobar -->
      <v-dialog v-model="approveModal" max-width="500">
        <v-card v-if="selectedItem" style="border-radius:12px; overflow:hidden">
          <!-- Header -->
          <div style="background: linear-gradient(135deg,#1b5e20,#2e7d32); padding: 24px 28px 20px;">
            <div class="d-flex align-center">
              <v-avatar size="52" color="rgba(255,255,255,0.2)" class="mr-4" style="border: 2px solid rgba(255,255,255,0.5)">
                <span style="color:#fff; font-weight:800; font-size:18px">{{ initials(selectedItem.name, selectedItem.surName) }}</span>
              </v-avatar>
              <div>
                <div class="white--text font-weight-bold" style="font-size:18px; letter-spacing:.3px">{{ selectedItem.name }} {{ selectedItem.surName }}</div>
                <div style="color:rgba(255,255,255,.7); font-size:12px; margin-top:2px">Solicitud de afiliación</div>
              </div>
            </div>
          </div>

          <!-- Info grid -->
          <v-card-text class="pa-5 pb-3">
            <div style="display:grid; grid-template-columns:1fr 1fr; gap:14px 20px;">

              <div>
                <div style="font-size:10px; font-weight:600; letter-spacing:.8px; color:rgba(255,255,255,0.5); text-transform:uppercase; margin-bottom:2px">Email</div>
                <div style="font-size:13px; color:rgba(255,255,255,0.92)">{{ selectedItem.email }}</div>
              </div>

              <div v-if="selectedItem.phone">
                <div style="font-size:10px; font-weight:600; letter-spacing:.8px; color:rgba(255,255,255,0.5); text-transform:uppercase; margin-bottom:2px">Teléfono</div>
                <div class="d-flex align-center" style="font-size:13px; color:rgba(255,255,255,0.92)">
                  {{ selectedItem.phone }}
                  <a :href="whatsappLink(selectedItem.phone)" target="_blank" style="display:inline-flex; align-items:center; margin-left:6px; color:#81c784; text-decoration:none; font-size:11px; font-weight:600">
                    <v-icon small color="green darken-1" style="margin-right:2px">chat</v-icon>WA
                  </a>
                </div>
              </div>

              <div v-if="selectedItem.num_document">
                <div style="font-size:10px; font-weight:600; letter-spacing:.8px; color:rgba(255,255,255,0.5); text-transform:uppercase; margin-bottom:2px">Cédula</div>
                <div style="font-size:13px; color:rgba(255,255,255,0.92)">{{ selectedItem.num_document }}</div>
              </div>

              <div v-if="selectedItem.birthDate">
                <div style="font-size:10px; font-weight:600; letter-spacing:.8px; color:rgba(255,255,255,0.5); text-transform:uppercase; margin-bottom:2px">Fecha de nacimiento</div>
                <div style="font-size:13px; color:rgba(255,255,255,0.92)">{{ formatDate(selectedItem.birthDate) }}</div>
              </div>

              <div v-if="selectedItem.department">
                <div style="font-size:10px; font-weight:600; letter-spacing:.8px; color:rgba(255,255,255,0.5); text-transform:uppercase; margin-bottom:2px">Ubicación</div>
                <div style="font-size:13px; color:rgba(255,255,255,0.92)">{{ selectedItem.department }}<span v-if="selectedItem.locality" style="color:rgba(255,255,255,0.5)"> · {{ selectedItem.locality }}</span></div>
              </div>

              <div v-if="selectedItem.address">
                <div style="font-size:10px; font-weight:600; letter-spacing:.8px; color:rgba(255,255,255,0.5); text-transform:uppercase; margin-bottom:2px">Dirección</div>
                <div style="font-size:13px; color:rgba(255,255,255,0.92)">{{ selectedItem.address }}</div>
              </div>

              <div v-if="selectedItem.type_affiliate">
                <div style="font-size:10px; font-weight:600; letter-spacing:.8px; color:rgba(255,255,255,0.5); text-transform:uppercase; margin-bottom:2px">Categoría</div>
                <div style="font-size:13px; color:rgba(255,255,255,0.92)">{{ selectedItem.type_affiliate }}</div>
              </div>

            </div>

            <div v-if="selectedItem.registrationComment" style="margin-top:16px; background:rgba(255,255,255,0.05); border-left:3px solid #43a047; border-radius:0 6px 6px 0; padding:10px 14px;">
              <div style="font-size:10px; font-weight:600; letter-spacing:.8px; color:rgba(255,255,255,0.5); text-transform:uppercase; margin-bottom:4px">Comentario</div>
              <div style="font-size:13px; color:rgba(255,255,255,0.82); line-height:1.5">{{ selectedItem.registrationComment }}</div>
            </div>

            <div style="margin-top:16px; padding:10px 14px; background:rgba(129,199,132,0.14); border:1px solid rgba(129,199,132,0.35); border-radius:8px; font-size:13px; color:#a5d6a7">
              Al confirmar, el afiliado será activado y recibirá un email de bienvenida.
            </div>
          </v-card-text>

          <v-card-actions class="pb-5 px-5">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" @click="approveModal = false">Cancelar</v-btn>
            <v-btn color="success" dark elevation="0" style="border-radius:8px; padding:0 20px" @click="doApprove()">
              <v-icon left small>check_circle</v-icon>Confirmar aprobación
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Modal Rechazar -->
      <v-dialog v-model="rejectModal" max-width="460">
        <v-card v-if="selectedItem">
          <div style="background: linear-gradient(135deg,#c62828,#e53935); padding: 20px 24px;">
            <div class="d-flex align-center">
              <v-avatar size="44" color="white" class="mr-3">
                <span style="color:#c62828; font-weight:800; font-size:15px">{{ initials(selectedItem.name, selectedItem.surName) }}</span>
              </v-avatar>
              <div>
                <div class="white--text font-weight-bold" style="font-size:16px">Rechazar solicitud</div>
                <div class="white--text" style="font-size:13px; opacity:.85">{{ selectedItem.name }} {{ selectedItem.surName }}</div>
              </div>
            </div>
          </div>
          <v-card-text class="pt-4 pb-2">
            <!-- Sin color hardcodeado: el backoffice corre siempre en modo oscuro
                 (plugins/vuetify.js), así que un gris de tema claro queda ilegible. -->
            <p class="text--secondary" style="font-size:14px">
              Indicá el motivo del rechazo. El afiliado quedará archivado y no podrá acceder al portal.
            </p>
            <v-textarea
              v-model="rejectReason"
              label="Motivo del rechazo"
              outlined
              no-resize
              rows="3"
              placeholder="Ej: Datos incompletos, no cumple requisitos..."
              :rules="[v => !!v || 'El motivo es obligatorio']"
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="pb-4 px-4">
            <v-spacer></v-spacer>
            <v-btn text @click="rejectModal = false">Cancelar</v-btn>
            <v-btn color="error" dark elevation="0" :disabled="!rejectReason.trim()" @click="doReject()">
              <v-icon left small>cancel</v-icon>Confirmar rechazo
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  data() {
    return {
      search: "",
      tab: 0,
      approveModal: false,
      rejectModal: false,
      selectedItem: null,
      rejectReason: "",
      pendingHeaders: [
        { text: "Acciones", value: "opciones", sortable: false, width: "160px" },
        { text: "Solicitante", value: "name", sortable: true },
        { text: "Teléfono", value: "phone", sortable: false },
        { text: "Ubicación", value: "department", sortable: true },
        { text: "Comentario", value: "registrationComment", sortable: false },
        { text: "Fecha solicitud", value: "createdAt", sortable: true },
      ],
      archivedHeaders: [
        { text: "Solicitante", value: "name", sortable: true },
        { text: "Fecha solicitud", value: "createdAt", sortable: true },
        { text: "Fecha rechazo", value: "rejectedAt", sortable: true },
        { text: "Rechazado por", value: "rejectedByName", sortable: true },
        { text: "Motivo", value: "rejectionReason", sortable: false },
      ],
      approvedHeaders: [
        { text: "Afiliado", value: "name", sortable: true },
        { text: "Categoría", value: "type_affiliate", sortable: true },
        { text: "Fecha solicitud", value: "createdAt", sortable: true },
        { text: "Fecha aprobación", value: "approvedAt", sortable: true },
        { text: "Aprobado por", value: "approvedByName", sortable: true },
      ],
    };
  },
  created() {
    this.getPendingAffiliates();
    this.getArchivedAffiliates();
  },
  computed: {
    ...mapState("affiliateNamespace", ["pendingAffiliates", "archivedAffiliates", "approvedAffiliates"]),
    filteredPending() {
      return Array.isArray(this.pendingAffiliates) ? this.pendingAffiliates : [];
    },
    filteredArchived() {
      return Array.isArray(this.archivedAffiliates) ? this.archivedAffiliates : [];
    },
    filteredApproved() {
      return Array.isArray(this.approvedAffiliates) ? this.approvedAffiliates : [];
    },
  },
  watch: {
    tab(val) {
      if (val === 0) this.getPendingAffiliates();
      else if (val === 1) this.getArchivedAffiliates();
      else if (val === 2) this.getApprovedAffiliates();
    },
  },
  methods: {
    ...mapActions("affiliateNamespace", [
      "getPendingAffiliates",
      "getArchivedAffiliates",
      "getApprovedAffiliates",
      "approveAffiliate",
      "rejectAffiliate",
    ]),
    initials(name, surName) {
      return `${(name || '').charAt(0)}${(surName || '').charAt(0)}`.toUpperCase();
    },
    confirmApprove(item) {
      this.selectedItem = item;
      this.approveModal = true;
    },
    openRejectModal(item) {
      this.selectedItem = item;
      this.rejectReason = "";
      this.rejectModal = true;
    },
    async doApprove() {
      await this.approveAffiliate(this.selectedItem._id);
      this.approveModal = false;
      this.selectedItem = null;
    },
    async doReject() {
      await this.rejectAffiliate({ id: this.selectedItem._id, reason: this.rejectReason.trim() });
      this.rejectModal = false;
      this.selectedItem = null;
      this.rejectReason = "";
    },
    formatDate(dateString) {
      if (!dateString) return "—";
      const d = new Date(dateString);
      return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
    },
    timeAgo(dateString) {
      if (!dateString) return "—";
      const diff = Math.floor((Date.now() - new Date(dateString)) / 1000);
      if (diff < 60) return "Hace un momento";
      if (diff < 3600) { const m = Math.floor(diff / 60); return `Hace ${m} minuto${m !== 1 ? 's' : ''}`; }
      if (diff < 86400) { const h = Math.floor(diff / 3600); return `Hace ${h} hora${h !== 1 ? 's' : ''}`; }
      if (diff < 172800) return "Ayer";
      if (diff < 604800) { const d = Math.floor(diff / 86400); return `Hace ${d} días`; }
      if (diff < 2592000) { const w = Math.floor(diff / 604800); return `Hace ${w} semana${w !== 1 ? 's' : ''}`; }
      if (diff < 31536000) { const mo = Math.floor(diff / 2592000); return `Hace ${mo} mes${mo !== 1 ? 'es' : ''}`; }
      const y = Math.floor(diff / 31536000); return `Hace ${y} año${y !== 1 ? 's' : ''}`;
    },
    whatsappLink(phone) {
      if (!phone) return "#";
      const digits = String(phone).replace(/\D/g, "").replace(/^0/, "");
      return `https://wa.me/598${digits}`;
    },
    categoryColor(cat) {
      const map = { BRONCE: "brown", PLATA: "blue-grey", ORO: "amber darken-2", PREMIUM: "purple" };
      return map[cat] || "grey";
    },
  },
};
</script>
